/**
 * Builds the Excel/Word/PDF export for the Security Interests register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered, ungrouped
 * register: one row per active interest, matching the table columns.
 *
 * Money is written as a real number (or blank when there is no figure) and
 * dates as real Date cells so the practitioner can sort / sum in Excel.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import type { SecurityInterestEntryUI } from "./types";

const COLLATERAL_CLASS_LABELS: Record<string, string> = {
  motor_vehicle: "Motor vehicle",
  allpap_no_exceptions: "ALLPAP (no exceptions)",
  allpap_with_exceptions: "ALLPAP (with exceptions)",
  other_goods: "Other goods",
  account: "Account",
  intangible_property: "Intangible property",
  financial_property: "Financial property",
  chattel_paper: "Chattel paper",
  general_intangible: "General intangible",
  inventory: "Inventory",
  negotiable_instrument: "Negotiable instrument",
};

const PARTY_TYPE_LABELS: Record<string, string> = {
  related_party: "Related party",
  third_party: "Third party",
};

function money(value: number | undefined): number | "" {
  return value == null ? "" : value;
}

function asDate(raw?: string): Date | "" {
  if (!raw) return "";
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
}

function identifierLabel(entry: SecurityInterestEntryUI): string {
  const { identifier, identifierType } = entry.securedParty;
  if (!identifier) return "";
  if (identifierType && identifierType !== "unknown") {
    return `${identifierType} ${identifier}`.trim();
  }
  return identifier;
}

function securityInterestsSheet(
  entries: SecurityInterestEntryUI[],
): ExportSheet {
  const sorted = [...entries]
    .filter((e) => e.status === "active" && !e.isExcluded)
    .sort((a, b) => {
      const byOwed = (b.owedAmount ?? 0) - (a.owedAmount ?? 0);
      if (byOwed !== 0) return byOwed;
      return a.securedParty.name.localeCompare(b.securedParty.name);
    });

  return {
    name: "Security Interests",
    headers: [
      "Secured party",
      "Identifier",
      "Status",
      "Party type",
      "Start date",
      "End date",
      "Class",
      "PMSI",
      "Est. owed",
    ],
    rows: sorted.map((entry) => [
      entry.securedParty.name,
      identifierLabel(entry),
      entry.registrationStatus === "unperfected" ? "Unperfected" : "Registered",
      PARTY_TYPE_LABELS[entry.partyType] ?? entry.partyType,
      asDate(entry.registrationStartDate),
      asDate(entry.registrationEndDate),
      COLLATERAL_CLASS_LABELS[entry.collateralClass] ?? entry.collateralClass,
      entry.pmsi ? "Yes" : "No",
      money(entry.owedAmount),
    ]),
  };
}

export function buildSecurityInterestsExport(
  entries: SecurityInterestEntryUI[],
): ExportContent {
  return {
    title: "Security Interests",
    sheets: [securityInterestsSheet(entries)],
  };
}
